import { DataSourceIDEnum, DataSourceType } from '@/models/dataSource';
import { CustomConnectorDto } from '@/models/connectors';

type SourceConnectorTypeMatcher = {
  ids?: string[];
  resolvers?: string[];
  formIds?: string[];
  namePatterns?: RegExp[];
};

const SOURCE_CONNECTOR_TYPE_MATCHERS: Partial<Record<DataSourceIDEnum, SourceConnectorTypeMatcher>> = {
  [DataSourceIDEnum.D365BusinessCentral]: {
    ids: [DataSourceIDEnum.D365BusinessCentral],
    resolvers: [DataSourceIDEnum.D365BusinessCentral],
    formIds: ['d365bc-auth-form', 'd365-bc-auth'],
    namePatterns: [/d365/i, /business\s*central/i],
  },
};

const getConnectorFormId = (connector: CustomConnectorDto) =>
  connector.authConfiguration?.configurations?.[0]?.elements?.auth?.reactJsonSchemaForm?.formId;

export const connectorMatchesDataSourceType = (connector: CustomConnectorDto, typeId: string): boolean => {
  const matcher = SOURCE_CONNECTOR_TYPE_MATCHERS[typeId as DataSourceIDEnum];
  if (!matcher) {
    return connector.id === typeId || connector.resolver === typeId;
  }

  if (matcher.ids?.includes(connector.id)) return true;
  if (connector.resolver && matcher.resolvers?.includes(connector.resolver)) return true;

  const formId = getConnectorFormId(connector);
  if (formId && matcher.formIds?.includes(formId)) return true;

  const connectorName = connector.name ?? '';
  if (matcher.namePatterns?.some((pattern) => pattern.test(connectorName))) return true;

  return false;
};

export const inferDataSourceTypeIdFromConnector = (connector: CustomConnectorDto): DataSourceIDEnum | undefined =>
  (Object.keys(SOURCE_CONNECTOR_TYPE_MATCHERS) as DataSourceIDEnum[]).find((typeId) =>
    connectorMatchesDataSourceType(connector, typeId)
  );

export const resolveDataSourceTypeForConnector = (
  connector: CustomConnectorDto,
  candidateTypes: DataSourceType[]
): DataSourceType | undefined => {
  const matchedType = candidateTypes.find((type) => connectorMatchesDataSourceType(connector, type.id));
  if (matchedType) return matchedType;

  const inferredTypeId = inferDataSourceTypeIdFromConnector(connector);
  if (!inferredTypeId) return undefined;

  return candidateTypes.find((type) => type.id === inferredTypeId);
};
